import { Alert, Stack, Text } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { formatMoney, TenantCurrency } from 'src/common/currency';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { FieldValue, HeldFeedField } from 'src/data-capture/types';
import { formatLongDate } from 'src/utils/dateFormatting';
import { DetectionState, heldFeedFieldsOfSelection } from '../stores/useDetectedEntitiesStore';

const IDENTIFIER_FIELDS = ['provider_reference_number', 'policy_number'];

const FALLBACK_FIELD_NAMES: Record<string, string> = {
  provider_reference_number: 'provider reference number',
  policy_number: 'policy number',
  value: 'value',
  value_date: 'value date',
};

// Each product names the field its own way: a defined benefit pension calls the provider
// reference number its "Pension scheme reference number".
const fieldName = (detection: DetectionState, field: string) => {
  const label = detection.metadata[field]?.label;
  return label
    ? `${label.charAt(0).toLowerCase()}${label.slice(1)}`
    : (FALLBACK_FIELD_NAMES[field] ?? field);
};

const displayValue = (field: string, value: FieldValue | null, currency: TenantCurrency) => {
  if (field === 'value') {
    return formatMoney(value as number | string, currency);
  }
  if (field === 'value_date') {
    return formatLongDate(String(value));
  }
  return String(value).trim();
};

const describe = (
  detection: DetectionState,
  held: HeldFeedField,
  currency: TenantCurrency,
): string => {
  const name = fieldName(detection, held.field);
  const documentValue = displayValue(held.field, held.documentValue, currency);
  if (!IDENTIFIER_FIELDS.includes(held.field)) {
    return `${detection.entityLabel}: this document has ${name} ${documentValue}, but the asset has ${displayValue(held.field, held.assetValue, currency)} from the data feed, so it isn't changed here.`;
  }
  if (held.assetValue == null || String(held.assetValue).trim() === '') {
    return `${detection.entityLabel}: this document has ${name} ${documentValue}, but the asset is connected to the data feed without one, so it can't be added here.`;
  }
  return `${detection.entityLabel}: this document has ${name} ${documentValue}, but the asset has ${displayValue(held.field, held.assetValue, currency)} and is connected to the data feed, so it can't be changed here.`;
};

type Props = {
  detections: DetectionState[];
};

export function HeldFeedFieldsNote({ detections }: Props) {
  const currency = useCurrency();
  const held = detections.flatMap(detection =>
    heldFeedFieldsOfSelection(detection).map(heldField => ({
      key: `${detection.id}-${heldField.field}`,
      field: heldField.field,
      sentence: describe(detection, heldField, currency),
    })),
  );
  if (held.length === 0) {
    return null;
  }
  const heldIdentifiers = held.filter(heldField => IDENTIFIER_FIELDS.includes(heldField.field));
  const assetsHoldingIdentifiers = detections.filter(detection =>
    heldFeedFieldsOfSelection(detection).some(heldField =>
      IDENTIFIER_FIELDS.includes(heldField.field),
    ),
  ).length;

  return (
    <Alert variant="light" color="blue" radius="lg" icon={<IconInfoCircle />} mb="xs">
      <Stack gap="xxs">
        {held.map(heldField => (
          <Text key={heldField.key} size="sm">
            {heldField.sentence}
          </Text>
        ))}
        {heldIdentifiers.length > 0 && (
          <Text size="sm">
            To change {heldIdentifiers.length === 1 ? 'it' : 'them'}, disconnect the{' '}
            {assetsHoldingIdentifiers === 1 ? 'asset' : 'assets'} from the data feed first.
          </Text>
        )}
      </Stack>
    </Alert>
  );
}
